
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { WardrobeItem as WardrobeItemType, AccessorySet, UserProfile, ItemAdjustment } from '../types';
import { urlToFile, hexToHsl, getFriendlyErrorMessage } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';
import Spinner from './Spinner';
import { TopsIcon, OuterwearIcon, BottomsIcon, ShoppingBagIcon, GlassesIcon, NecklaceIcon, LayersIcon, PlusIcon, ChevronDownIcon, CheckCircleIcon, DressIcon, ShoesIcon, SearchIcon, LockIcon, UploadCloudIcon, SparklesIcon } from './icons';
import WardrobeItemDetailsModal from './AddProductModal';
import { WardrobeItem } from './WardrobeItem';
import PremiumFeatureModal from './PremiumFeatureModal';
import { CDN_URLS } from '../config';
import { generateFashionImage } from '../services/geminiService';
import { checkGenerationQuota, incrementGenerationCount } from '@shared/services/userService';
import { MEMBERSHIP_LIMITS } from '../constants';
import { getMultiCdnUrl } from '../utils/imageUtils';

interface WardrobePanelProps {
    onItemSelect: (itemFile: File, itemInfo: WardrobeItemType, initialAdjustments?: ItemAdjustment) => Promise<void>;
    onFindSimilar: (item: WardrobeItemType) => void;
    activeItemIds: string[];
    isLoading: boolean;
    wardrobe: WardrobeItemType[];
    onApplyAccessorySet: (set: AccessorySet) => void;
    predefinedAccessorySets: AccessorySet[];
    userProfile: UserProfile;
    onAddItem: (item: WardrobeItemType) => void;
    currentUserId: string; // Ensure this is passed
}

const categoryIcons: { [key: string]: React.FC<any> } = {
    Tops: TopsIcon,
    Jackets: OuterwearIcon,
    Dresses: DressIcon,
    Bottoms: BottomsIcon,
    Shoes: ShoesIcon,
    Bags: ShoppingBagIcon,
    Eyewear: GlassesIcon,
    Jewelry: NecklaceIcon,
    Other: LayersIcon,
};


export const WardrobePanel: React.FC<WardrobePanelProps> = ({
    onItemSelect,
    onFindSimilar,
    activeItemIds,
    isLoading,
    wardrobe,
    onApplyAccessorySet,
    predefinedAccessorySets,
    userProfile,
    onAddItem,
    currentUserId
}) => {
    const { t, lang } = useTranslation();
    const [activeTab, setActiveTab] = useState<'garments' | 'accessories' | 'sets'>('garments');
    const [error, setError] = useState<string | null>(null);
    const [loadingItemId, setLoadingItemId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeGarmentCategory, setActiveGarmentCategory] = useState<string>('All');
    const [activeAccessoryCategory, setActiveAccessoryCategory] = useState<string>('All');
    const [activeBrand, setActiveBrand] = useState<string>('All');
    const [sortOption, setSortOption] = useState<'category' | 'name' | 'color' | 'brand'>('category');
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedItemForDetails, setSelectedItemForDetails] = useState<WardrobeItemType | null>(null);

    // AI Generation State
    const [generationPrompt, setGenerationPrompt] = useState('');
    const [generatedCategory, setGeneratedCategory] = useState<string>('Tops');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState<string | null>(null);
    const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
    const [requiredLevelForModal, setRequiredLevelForModal] = useState('Premium');

    const { garments, accessories } = useMemo(() => {
        // Show items of type 'garment' OR category 'Shoes' in the garments tab.
        const garments = wardrobe.filter(item => item.type === 'garment' || item.category === 'Shoes');
        // Show accessories that are NOT shoes in the accessories tab.
        const accessories = wardrobe.filter(item => item.type === 'accessory' && item.category !== 'Shoes');
        return { garments, accessories };
    }, [wardrobe]);

    const uniqueGarmentCategories = useMemo(() =>
        ['All', ...Array.from(new Set(garments.map(g => g.category || 'Other')))
            .sort((a, b) => t(`wardrobe_category_${a}` as any).localeCompare(t(`wardrobe_category_${b}` as any), lang))
        ], [garments, t, lang]);
    const uniqueAccessoryCategories = useMemo(() =>
        ['All', ...Array.from(new Set(accessories.map(a => a.category || 'Other')))
            .sort((a, b) => t(`wardrobe_category_${a}` as any).localeCompare(t(`wardrobe_category_${b}` as any), lang))
        ], [accessories, t, lang]);
    const uniqueBrands = useMemo(() => ['All', ...Array.from(new Set(wardrobe.map(item => item.brand).filter(Boolean) as string[])).sort()], [wardrobe]);

    const sortItems = (items: WardrobeItemType[]) => {
        return [...items].sort((a, b) => {
            if (sortOption === 'name') {
                return t(a.nameKey).localeCompare(t(b.nameKey), lang);
            }
            if (sortOption === 'color') {
                const colorA = a.defaultColor ? hexToHsl(a.defaultColor).h : 361; // Items without color at the end
                const colorB = b.defaultColor ? hexToHsl(b.defaultColor).h : 361;
                return colorA - colorB;
            }
            if (sortOption === 'brand') {
                return (a.brand || 'zzzz').localeCompare(b.brand || 'zzzz', lang);
            }
            return 0; // Default (category) sorting is handled by grouping and sorting groups
        });
    };

    const garmentCategories = useMemo(() => {
        let filtered = garments;
        if (activeGarmentCategory !== 'All') {
            filtered = filtered.filter(item => (item.category || 'Other') === activeGarmentCategory);
        }
        if (searchQuery.trim() !== '') {
            filtered = filtered.filter(item => t(item.nameKey).toLowerCase().includes(searchQuery.toLowerCase()));
        }
        if (activeBrand !== 'All') {
            filtered = filtered.filter(item => item.brand === activeBrand);
        }

        const sorted = sortItems(filtered);

        const cats = sorted.reduce<Record<string, WardrobeItemType[]>>((acc, item) => {
            const category = item.category || 'Other';
            if (!acc[category]) acc[category] = [];
            acc[category].push(item);
            return acc;
        }, {});

        return Object.entries(cats).sort(([catA], [catB]) =>
            t(`wardrobe_category_${catA}` as any).localeCompare(t(`wardrobe_category_${catB}` as any), lang)
        );
    }, [garments, activeGarmentCategory, searchQuery, t, sortOption, activeBrand, lang]);

    const accessoryCategories = useMemo(() => {
        let filtered = accessories;
        if (activeAccessoryCategory !== 'All') {
            filtered = filtered.filter(item => (item.category || 'Other') === activeAccessoryCategory);
        }
        if (searchQuery.trim() !== '') {
            filtered = filtered.filter(item => t(item.nameKey).toLowerCase().includes(searchQuery.toLowerCase()));
        }
        if (activeBrand !== 'All') {
            filtered = filtered.filter(item => item.brand === activeBrand);
        }

        const sorted = sortItems(filtered);

        const cats = sorted.reduce<Record<string, WardrobeItemType[]>>((acc, item) => {
            const category = item.category || 'Other';
            if (!acc[category]) acc[category] = [];
            acc[category].push(item);
            return acc;
        }, {});

        return Object.entries(cats).sort(([catA], [catB]) =>
            t(`wardrobe_category_${catA}` as any).localeCompare(t(`wardrobe_category_${catB}` as any), lang)
        );
    }, [accessories, activeAccessoryCategory, searchQuery, t, sortOption, activeBrand, lang]);


    const handleApplyAndClose = async (item: WardrobeItemType, adjustments?: ItemAdjustment) => {
        if (isLoading || loadingItemId) return;
        setError(null);
        setLoadingItemId(item.id);
        setIsDetailsModalOpen(false); // Close modal on apply
        try {
            const fullUrl = getMultiCdnUrl(item.url, 'WARDROBE');
            const { file, item: updatedItem } = await urlToFile(fullUrl, item.id, item.defaultColor, item.type, item.category, item.iconUrl, item.nameKey);
            await onItemSelect(file, updatedItem, adjustments);
        } catch (err) {
            setError(getFriendlyErrorMessage(err, t('wardrobePanel_itemLoadErrorContext'), lang, t));
            console.error(`[WardrobePanel] Error loading item ${t(item.nameKey)}:`, err);
        } finally {
            setLoadingItemId(null);
        }
    };

    const handleGarmentFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (!file.type.startsWith('image/')) {
                setError(t('startScreen_uploadErrorFileTypeError'));
                return;
            }
            setError(null);

            const customGarmentInfo: WardrobeItemType = {
                id: `custom-garment-${Date.now()}`,
                nameKey: file.name.split('.').slice(0, -1).join('.') || file.name,
                url: URL.createObjectURL(file),
                type: 'garment',
                category: 'Tops', // Defaulting to Tops for now
            };

            setLoadingItemId(customGarmentInfo.id);
            try {
                await onItemSelect(file, customGarmentInfo);
            } catch (err) {
                setError(getFriendlyErrorMessage(err, t('wardrobePanel_itemLoadErrorContext'), lang, t));
                console.error(`[WardrobePanel] Error loading custom garment:`, err);
            } finally {
                setLoadingItemId(null);
                e.target.value = ''; // Clear file input
            }
        }
    };

    const handleAccessoryFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (!file.type.startsWith('image/')) {
                setError(t('startScreen_uploadErrorFileTypeError'));
                return;
            }
            setError(null);

            const customAccessoryInfo: WardrobeItemType = {
                id: `custom-acc-${Date.now()}`,
                nameKey: file.name.split('.').slice(0, -1).join('.') || file.name,
                url: URL.createObjectURL(file),
                type: 'accessory',
                category: 'Other',
            };

            setLoadingItemId(customAccessoryInfo.id);
            try {
                await onItemSelect(file, customAccessoryInfo);
            } catch (err) {
                setError(getFriendlyErrorMessage(err, t('wardrobePanel_itemLoadErrorContext'), lang, t));
                console.error(`[WardrobePanel] Error loading custom accessory:`, err);
            } finally {
                setLoadingItemId(null);
                e.target.value = ''; // Clear file input
            }
        }
    };

    const isPremiumFeatureLocked = (requiredLevel: string | undefined, profile: UserProfile): boolean => {
        if (!requiredLevel) return false;

        const tiers = ['free', 'basic', 'premium', 'nextgen', 'advanced'];
        const userLevel = (profile.membershipLevel || 'free').toLowerCase();
        const required = requiredLevel.toLowerCase();

        const expiryDate = profile.membershipExpiry ? new Date(profile.membershipExpiry + 'T23:59:59') : null;
        const isExpired = expiryDate ? expiryDate < new Date() : false;

        // Any paid membership is invalid if expired, reverting to free
        const effectiveUserLevel = isExpired ? 'free' : userLevel;

        const userScore = tiers.indexOf(effectiveUserLevel);
        const requiredScore = tiers.indexOf(required);

        // If tier not found, default to basic's score or 0
        if (userScore === -1) return true;

        return userScore < requiredScore;
    };

    const handleItemCardClick = (item: WardrobeItemType) => {
        if (isLoading || loadingItemId || activeItemIds.includes(item.id)) return;

        if (isPremiumFeatureLocked(item.requiredMembership, userProfile)) {
            setRequiredLevelForModal(item.requiredMembership === 'premium' ? t('profilePage_membership_premium') : t('profilePage_membership_advanced'));
            setIsPremiumModalOpen(true);
            return;
        }

        setSelectedItemForDetails(item);
        setIsDetailsModalOpen(true);
    };

    const handleGenerateItem = async () => {
        if (!generationPrompt.trim() || isGenerating) return;

        // --- QUOTA CHECK ---
        if (!checkGenerationQuota(userProfile)) {
            const level = (userProfile.membershipLevel?.toLowerCase() || 'basic') as keyof typeof MEMBERSHIP_LIMITS;
            setGenerationError(t('error_quota_exceeded', { limit: String(MEMBERSHIP_LIMITS[level]) }));
            return;
        }
        // -------------------

        setIsGenerating(true);
        setGenerationError(null);
        try {
            const base64Data = await generateFashionImage(generationPrompt);

            const dataUrl = `data:image/png;base64,${base64Data}`;
            const res = await fetch(dataUrl);
            const blob = await res.blob();
            const fileName = `ai-${generationPrompt.substring(0, 20).replace(/\s/g, '_')}.png`;
            const file = new File([blob], fileName, { type: 'image/png' });

            // Determine item type based on category for simplicity, or default to garment.
            // In a more complex app, AI could predict type.
            const isAccessoryType = ['Shoes', 'Bags', 'Eyewear', 'Jewelry', 'Other'].includes(generatedCategory);

            const newItem: WardrobeItemType = {
                id: `ai-${Date.now()}`,
                nameKey: t('wardrobe_item_ai_name_prefix', { prompt: generationPrompt.substring(0, 30) }),
                url: dataUrl,
                type: isAccessoryType ? 'accessory' : 'garment',
                category: generatedCategory,
                originalFile: file,
                brand: 'AI Generated' // This key will be translated in the UI
            };

            // --- INCREMENT QUOTA ---
            incrementGenerationCount(currentUserId);
            // ----------------------

            onAddItem(newItem);
            setGenerationPrompt('');
        } catch (err) {
            setGenerationError(getFriendlyErrorMessage(err, t('wardrobe_ai_studio_error'), lang, t));
        } finally {
            setIsGenerating(false);
        }
    };

    const tabContentVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10 },
    };

    const canUseAIGeneration = userProfile.membershipLevel === 'advanced';

    const renderCategory = (category: string, items: WardrobeItemType[]) => {
        const IconComponent = categoryIcons[category] || categoryIcons['Other'];
        const hasPremiumAccess = userProfile.membershipLevel === 'premium' || userProfile.membershipLevel === 'advanced';

        return (
            <details key={category} open className="group border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                <summary className="list-none flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 flex items-center">
                        <IconComponent className="w-5 h-5 mr-3 text-gray-500 dark:text-gray-400" />
                        {t(`wardrobe_category_${category}` as any, undefined) || category}
                    </h3>
                    <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform group-open:rotate-180" />
                </summary>
                <div className="grid grid-cols-3 gap-2 p-2">
                    {activeTab === 'garments' && category === 'Tops' && (
                        <div
                            title={t('wardrobe_upload_garment_title')}
                            className="relative flex flex-col text-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 transition-colors duration-300 hover:border-indigo-500 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                            <label
                                htmlFor="custom-garment-upload"
                                className="w-full aspect-square flex flex-col items-center justify-center cursor-pointer p-2"
                            >
                                <UploadCloudIcon className="w-6 h-6 text-gray-400 mb-1" />
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{t('wardrobe_upload_garment')}</span>
                                <input
                                    id="custom-garment-upload"
                                    type="file"
                                    className="hidden"
                                    accept="image/png, image/jpeg, image/webp, image/avif, image/heic, image/heif"
                                    onChange={handleGarmentFileUpload}
                                    disabled={isLoading}
                                />
                            </label>
                        </div>
                    )}
                    {activeTab === 'accessories' && category === 'Other' && (
                        <div
                            title={t('wardrobe_upload_accessory_title')}
                            className="relative flex flex-col text-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 transition-colors duration-300 hover:border-indigo-500 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                            <label
                                htmlFor="custom-accessory-upload"
                                className="w-full aspect-square flex flex-col items-center justify-center cursor-pointer p-2"
                            >
                                <UploadCloudIcon className="w-6 h-6 text-gray-400 mb-1" />
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{t('wardrobe_upload_accessory')}</span>
                                <input
                                    id="custom-accessory-upload"
                                    type="file"
                                    className="hidden"
                                    accept="image/png, image/jpeg, image/webp, image/avif, image/heic, image/heif"
                                    onChange={handleAccessoryFileUpload}
                                    disabled={isLoading}
                                />
                            </label>
                        </div>
                    )}
                    {items.map((item: WardrobeItemType) => {
                        const isItemLoading = loadingItemId === item.id;
                        const isActive = activeItemIds.includes(item.id);
                        const isLocked = isPremiumFeatureLocked(item.requiredMembership, userProfile);

                        return (
                            <WardrobeItem
                                key={item.id}
                                item={item}
                                isActive={isActive}
                                isItemLoading={isItemLoading}
                                isLoading={isLoading}
                                isLocked={isLocked}
                                hasPremiumAccess={hasPremiumAccess}
                                onFindSimilar={onFindSimilar}
                                onItemClick={handleItemCardClick}
                                t={t}
                            />
                        );
                    })}
                </div>
            </details>
        );
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-white">
                    {t('wardrobe_title')}
                </h2>
            </div>

            {/* AI Studio */}
            <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-2">
                    <SparklesIcon className="w-5 h-5 text-indigo-500" />
                    {t('wardrobe_ai_studio_title')}
                </h3>
                {!canUseAIGeneration ? (
                    <div className="text-center p-4 bg-gray-100 dark:bg-gray-700 rounded-md">
                        <LockIcon className="w-6 h-6 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm font-semibold">{t('feature_locked_advanced')}</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <select
                            value={generatedCategory}
                            onChange={(e) => setGeneratedCategory(e.target.value)}
                            className="w-full text-sm p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 mb-2"
                            disabled={isGenerating}
                            aria-label={t('wardrobe_ai_studio_category_label') || "Select category"}
                        >
                            <option value="Tops">{t('wardrobe_category_Tops')}</option>
                            <option value="Bottoms">{t('wardrobe_category_Bottoms')}</option>
                            <option value="Dresses">{t('wardrobe_category_Dresses')}</option>
                            <option value="Jackets">{t('wardrobe_category_Jackets')}</option>
                            <option value="Shoes">{t('wardrobe_category_Shoes')}</option>
                            <option value="Bags">{t('wardrobe_category_Bags')}</option>
                            <option value="Eyewear">{t('wardrobe_category_Eyewear')}</option>
                            <option value="Jewelry">{t('wardrobe_category_Jewelry')}</option>
                            <option value="Other">{t('wardrobe_category_Other')}</option>
                        </select>
                        <textarea
                            value={generationPrompt}
                            onChange={e => setGenerationPrompt(e.target.value)}
                            placeholder={t('wardrobe_ai_studio_placeholder')}
                            rows={2}
                            className="w-full text-sm p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 resize-none"
                            disabled={isGenerating}
                        />
                        <button
                            onClick={handleGenerateItem}
                            disabled={isGenerating || !generationPrompt.trim()}
                            className="w-full flex items-center justify-center bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {isGenerating ? <Spinner className="w-5 h-5 mr-2" /> : null}
                            {isGenerating ? t('wardrobe_ai_studio_generating') : t('wardrobe_ai_studio_generate')}
                        </button>
                        {generationError && <p className="text-red-500 text-xs mt-1">{generationError}</p>}
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
                <button onClick={() => setActiveTab('garments')} className={`px-4 py-2 font-semibold ${activeTab === 'garments' ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'text-gray-500'}`}>{t('wardrobe_tab_garments')}</button>
                <button onClick={() => setActiveTab('accessories')} className={`px-4 py-2 font-semibold ${activeTab === 'accessories' ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'text-gray-500'}`}>{t('wardrobe_tab_accessories')}</button>
                <button onClick={() => setActiveTab('sets')} className={`px-4 py-2 font-semibold ${activeTab === 'sets' ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'text-gray-500'}`}>{t('wardrobe_tab_sets')}</button>
            </div>

            {/* Filters */}
            <div className="mb-4 space-y-3">
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t('wardrobe_searchPlaceholder')} className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
                <div className="grid grid-cols-3 gap-2 text-sm">
                    <select value={sortOption} onChange={e => setSortOption(e.target.value as any)} className="p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" aria-label={t('wardrobe_sort_label') || "Sort items"}>
                        <option value="category">{t('wardrobe_sort_category')}</option>
                        <option value="name">{t('wardrobe_sort_name')}</option>
                        <option value="color">{t('wardrobe_sort_color')}</option>
                        <option value="brand">{t('wardrobe_sort_brand')}</option>
                    </select>
                    <select value={activeBrand} onChange={e => setActiveBrand(e.target.value)} className="p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" aria-label={t('wardrobe_filter_brand_label') || "Filter by brand"}>
                        {uniqueBrands.map(brand => <option key={brand} value={brand}>{brand === 'All' ? t('wardrobe_allBrands') : (brand === 'AI Generated' ? t('wardrobe_brand_ai_generated') : brand)}</option>)}
                    </select>
                    {activeTab === 'garments' && (
                        <select value={activeGarmentCategory} onChange={e => setActiveGarmentCategory(e.target.value)} className="p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" aria-label={t('wardrobe_filter_category_label') || "Filter by category"}>
                            {uniqueGarmentCategories.map(cat => <option key={cat} value={cat}>{cat === 'All' ? t('wardrobe_all_categories') : (t(`wardrobe_category_${cat}` as any, undefined) || cat)}</option>)}
                        </select>
                    )}
                    {activeTab === 'accessories' && (
                        <select value={activeAccessoryCategory} onChange={e => setActiveAccessoryCategory(e.target.value)} className="p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" aria-label={t('wardrobe_filter_category_label') || "Filter by category"}>
                            {uniqueAccessoryCategories.map(cat => <option key={cat} value={cat}>{cat === 'All' ? t('wardrobe_all_categories') : (t(`wardrobe_category_${cat}` as any, undefined) || cat)}</option>)}
                        </select>
                    )}
                </div>
            </div>

            {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

            {/* Content */}
            <div className="overflow-y-auto custom-scrollbar flex-grow -mr-4 pr-4">
                <AnimatePresence mode="wait">
                    <motion.div key={activeTab} variants={tabContentVariants} initial="hidden" animate="visible" exit="exit">
                        {activeTab === 'garments' && (
                            <div className="space-y-4">
                                {garmentCategories.length > 0 ? garmentCategories.map(([category, items]) => renderCategory(category, items)) : <p className="text-center text-gray-500">{t('wardrobe_no_results')}</p>}
                            </div>
                        )}
                        {activeTab === 'accessories' && (
                            <div className="space-y-4">
                                {accessoryCategories.length > 0 ? accessoryCategories.map(([category, items]) => renderCategory(category, items)) : <p className="text-center text-gray-500">{t('wardrobe_no_results')}</p>}
                            </div>
                        )}
                        {activeTab === 'sets' && (
                            <div className="space-y-4">
                                {predefinedAccessorySets.map(set => (
                                    <button key={set.id} onClick={() => onApplyAccessorySet(set)} className="w-full flex items-center gap-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                        <img
                                            src={getMultiCdnUrl(set.previewImageUrl, 'WARDROBE')}
                                            alt={t(set.nameKey)}
                                            className="w-20 h-20 object-cover rounded-md"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        <div>
                                            <p className="font-semibold text-left">{t(set.nameKey)}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 text-left">{t('wardrobe_set_includes', { count: set.accessoryIds.length })}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <WardrobeItemDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                item={selectedItemForDetails}
                onApply={handleApplyAndClose}
                isLoading={!!loadingItemId}
                isActive={!!selectedItemForDetails && activeItemIds.includes(selectedItemForDetails.id)}
            />

            <PremiumFeatureModal
                isOpen={isPremiumModalOpen}
                onClose={() => setIsPremiumModalOpen(false)}
                requiredLevel={requiredLevelForModal}
            />
        </div>
    );
};
